import React from 'react';
import { CalendarDays, MapPin, Phone, Mail, MessageSquare, Sparkles, BedDouble, Wifi, Wine, Anchor } from 'lucide-react';
import MsRubiesBadge from '../components/MsRubiesBadge';

const UPCOMING_TRIPS = [
    {
        id: 'family-friends-2027',
        eyebrow: '2nd Annual Group Cruise',
        title: 'Family & Friends Trip',
        dates: 'July 25 – Aug 1, 2027',
        line: 'Western Caribbean Cruise',
        ports: [
            { name: 'Cozumel', country: 'Mexico' },
            { name: 'Costa Maya', country: 'Mexico' },
            { name: 'Isla de Roatan', country: 'Honduras' },
        ],
        includes: [
            { icon: Wine, label: 'Drink package included' },
            { icon: Wifi, label: 'WiFi included' },
        ],
        cabins: [
            { name: 'Interior', price: '$1,060.79 pp' },
            { name: 'Ocean View', price: '$1,160.79 pp' },
            { name: 'Balcony', price: '$1,250.79 pp' },
        ],
        payments: [
            { label: 'Remove cabin from inventory', amount: '$50 per cabin', due: 'Due July 25, 2026' },
            { label: 'Per-person deposit', amount: '$199 per person', due: 'Due October 15, 2026' },
            { label: 'Final balance', amount: 'Remaining', due: 'Due April 26, 2027' },
        ],
        note: 'Please contact Ms. Ruby directly for cabins with more than 2 guests.',
        image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=2400&q=85',
    },
];

export default function UpcomingPage() {
    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
            <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-end mb-14">
                <div className="max-w-3xl">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-aqua mb-3">Cruise Vibes · Upcoming Events</div>
                    <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter">
                        Sail with <span className="font-serif-italic font-medium text-brand-gold">us</span>.
                    </h1>
                    <p className="text-white/70 mt-5 text-base">
                        Our hand-picked group cruises sell out fast. Lock in your cabin early, link your crew, and let Ms. Ruby handle the rest.
                    </p>
                </div>
                <MsRubiesBadge variant="stamp-light" className="lg:ml-auto" />
            </div>

            <div className="space-y-12" data-testid="upcoming-trips">
                {UPCOMING_TRIPS.map((t) => (
                    <article
                        key={t.id}
                        data-testid={`upcoming-trip-${t.id}`}
                        className="grid lg:grid-cols-[440px_1fr] bg-brand-surface border border-white/10 rounded-3xl overflow-hidden"
                    >
                        <div className="relative min-h-[280px] lg:min-h-full">
                            <img src={t.image} alt={t.title} className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-brand-bg/85 via-brand-bg/20 to-transparent" />
                            <div className="absolute top-6 left-6 right-6">
                                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-brand-aqua bg-brand-bg/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">
                                    <Sparkles size={12} /> {t.eyebrow}
                                </div>
                            </div>
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white leading-tight">
                                    {t.title}
                                </div>
                                <div className="text-sm text-white/70 mt-2 font-serif-italic">{t.line}</div>
                            </div>
                        </div>

                        <div className="p-8 lg:p-10 flex flex-col gap-6">
                            <div className="grid sm:grid-cols-3 gap-5">
                                <Stat icon={CalendarDays} label="Dates" value={t.dates} />
                                <Stat icon={Anchor} label="Departing From" value="Galveston, TX" />
                                <Stat
                                    icon={MapPin}
                                    label="Ports of Call"
                                    value={
                                        <div className="space-y-1">
                                            {t.ports.map((p) => (
                                                <div key={p.name}>
                                                    <span className="text-white">{p.name}</span>
                                                    <span className="text-white/55"> · {p.country}</span>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                />
                            </div>

                            <div className="flex flex-wrap gap-3">
                                {t.includes.map((inc) => (
                                    <div
                                        key={inc.label}
                                        className="inline-flex items-center gap-2 bg-brand-aqua/10 border border-brand-aqua/30 rounded-full px-4 py-2 text-xs uppercase tracking-widest text-brand-aqua font-semibold"
                                    >
                                        <inc.icon size={14} /> {inc.label}
                                    </div>
                                ))}
                            </div>

                            <div>
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
                                    <BedDouble size={12} className="text-brand-gold" /> Cabin Pricing
                                </div>
                                <div className="grid sm:grid-cols-3 gap-3">
                                    {t.cabins.map((c) => (
                                        <div
                                            key={c.name}
                                            data-testid={`cabin-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                                            className="bg-brand-bg/40 border border-white/10 rounded-xl p-4"
                                        >
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-aqua">{c.name}</div>
                                            <div className="font-display text-xl font-bold text-brand-gold mt-1">{c.price}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
                                    <Sparkles size={12} className="text-brand-pink" /> Payment Schedule
                                </div>
                                <ul className="space-y-2">
                                    {t.payments.map((p, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-4 text-sm bg-brand-bg/30 border border-white/5 rounded-lg px-4 py-3"
                                        >
                                            <span className="h-6 w-6 rounded-full bg-brand-pink/20 border border-brand-pink/40 text-brand-pink text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white font-medium">{p.label} — <span className="text-brand-gold">{p.amount}</span></div>
                                                <div className="text-[11px] uppercase tracking-widest text-white/50 mt-0.5">{p.due}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-2xl px-5 py-4 text-sm text-white/85">
                                <i className="fa-solid fa-users mr-2 text-brand-gold"></i>
                                {t.note}
                            </div>

                            <div className="border-t border-white/10 pt-5">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-aqua mb-3">Reserve with Ms. Ruby</div>
                                <div className="flex flex-wrap gap-3">
                                    <a href="tel:+16029354489" data-testid="upcoming-call" className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-brand-aqua/15 border border-white/10 hover:border-brand-aqua/40 px-4 h-10 text-xs uppercase tracking-widest text-white transition-colors">
                                        <Phone size={14} className="text-brand-aqua" /> Call
                                    </a>
                                    <a href="sms:+16029354489" data-testid="upcoming-text" className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-brand-pink/15 border border-white/10 hover:border-brand-pink/40 px-4 h-10 text-xs uppercase tracking-widest text-white transition-colors">
                                        <MessageSquare size={14} className="text-brand-pink" /> Text
                                    </a>
                                    <a href="mailto:rubiesglobalservices@gmail.com?subject=Family%20%26%20Friends%20Trip%20%E2%80%94%20July%2025-Aug%201%2C%202027" data-testid="upcoming-email" className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-brand-gold/15 border border-white/10 hover:border-brand-gold/40 px-4 h-10 text-xs uppercase tracking-widest text-white transition-colors">
                                        <Mail size={14} className="text-brand-gold" /> Email
                                    </a>
                                </div>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1.5">
                <Icon size={12} className="text-brand-aqua" /> {label}
            </div>
            <div className="text-sm text-white/90 font-medium leading-tight">{value}</div>
        </div>
    );
}
